// PostVideoPlayer.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { format } from 'date-fns';
import throttle from 'lodash/throttle';
import { withScope, captureException, addBreadcrumb } from '@sentry/browser';

import DecryptionWrapper from '../container/DecryptionWrapper.js';
import MessageVideoPlayer from '../container/MessageVideoPlayer.js';
import FlixPlayer from '../container/FlixPlayer.js';
import { LoadMode } from '../resource/playerConfig.js';
import StatefulImage from '../component/StatefulImage.jsx';
import withPlayerTrackingEvent, {
  VIDEO_CATEGORY,
} from '../resource/withPlayerTrackingEvent.js';

import getImageSupport from '../resource/getImageSupport.js';
import getPublicSrcSet from '../resource/getPublicSrcSet.js';
import { getTimeFormat, PLAY_STATE } from '../resource/playerUtils.js';
import { DrmFeatureName } from '../resource/drmConstants.js';
import { EventTypes } from '../resource/trackingConstants.js';
import { player as playerDebug } from '../resource/debug.js';
import { Severity } from '../resource/sentry.js';

import FadeIn from '../style/FadeIn.js';
import media from '../style/media.js';

import GridVideoSource from '../../img/ic_grid_video.svg';
import { ResetButtonStyle } from '../style/ResetButtonStyle.js';
import PlayIcon from '../../img/ic_play_60.svg';
import { PlayerType } from '../resource/engagementConstants.js';

const playerDebugLog = playerDebug.extend('log:PostVideoPlayer');
const playerError = playerDebug.extend('error:PostVideoPlayer');
const playerLog = ({ messages, data }) => {
  addBreadcrumb({
    category: 'player',
    level: Severity.Info,
    message: messages.join(' '),
    data,
  });
  playerDebugLog(...messages, data);
};

export class PostVideoPlayer extends React.PureComponent {
  state = {
    currentTime: 0,
    duration: 0,
    hasMediaPlayed: false,
    shouldHidePoster: false,
    shouldShowPlayButton: false,
    playState: PLAY_STATE.Pause,
  };
  videoOption = {};
  imageOption = {};
  player = null;
  posterTarget = null;
  posterFormat = getImageSupport().bestFormat;

  handleEngagementData = ({ isPlaying, isPlayerVisible }) => {
    const {
      updateEngagementData,
      isFree,
      isNewlyUnlocked,
      viewScope,
      mediaType,
      postId,
      senderId,
      senderName,
    } = this.props;

    if (this.player?.getAssetUri()) {
      updateEngagementData({
        playerType: PlayerType.VOD,
        isFree,
        isNewlyUnlocked,
        viewScope,
        mediaType,
        messageId: postId,
        playerSource: this.player?.getAssetUri(),
        isPlaying,
        isPlayerVisible,
        creatorId: senderId,
        creatorUsername: senderName,
      });
    }
  };

  handlePlayerSetup = player => {
    const { timestampOffsetMilliseconds } = this.props;
    this.player = player;
    this.props.trackPlayerEvent({
      type: EventTypes.PLAYER_VISIBLE,
      timestamp: Date.now() + timestampOffsetMilliseconds,
    });
    this.handleEngagementData({ isPlayerVisible: true });
    this.attachListeners();
    this.handleCanPlay();
  };

  handleLoadedMetadata = () => {
    // Somehow Safari doesn't fire `canplay` on iOS,
    // so we leverage the `loadedmetadata` to trigger video play after loading source.
    // (have tested on MAC/Chrome, MAC/Safari, Android/Chrome, iOS/Safari)
    // reference: https://github.com/video-dev/hls.js/issues/1686
    this.handleCanPlay();
    this.setState({ duration: this.player?.duration() });
  };

  handleCanPlay = () => {
    const { shouldAutoplayPost, isActive, setCurrentAutoplayPostId, postId } =
      this.props;
    if (!this.player) {
      return;
    }
    if (isActive && shouldAutoplayPost) {
      setCurrentAutoplayPostId(postId);
      this.player
        ?.play()
        .then(() => {
          playerLog({
            messages: ['handleCanPlay', 'play() was invoked successfully'],
          });
          this.setState({ shouldShowPlayButton: false });
          // Use playState to check and to prevent pause, if play() not complete
        })
        .catch(error => {
          this.setState({
            shouldShowPlayButton: true,
            playState: PLAY_STATE.Pause,
            loadState: null,
          });
          playerError('handleCanPlay - failed to execute play()', error);
          if (error.name === 'NotAllowedError') {
            return;
          }
          withScope(scope => {
            scope.setFingerprint(['post-video-player', error.name]);
            scope.setExtra('action', 'canplay event');
            scope.setExtra('playState', this.state.playState);
            scope.setExtra('loadState', this.state.loadState);
            captureException(error);
          });
        });
    } else if (isActive) {
      const { playState } = this.state;
      if (this.player && playState !== PLAY_STATE.Pause) {
        this.player?.pause();
      }
    }
  };

  handleImageOnClick = event => {
    const {
      postId,
      assetId,
      isFullscreenModalOpened,
      enabledImageFullscreenModal,
      togglePostAssetFullscreenModal,
    } = this.props;
    if (enabledImageFullscreenModal && !isFullscreenModalOpened) {
      event.stopPropagation();
      return togglePostAssetFullscreenModal({
        postId,
        clickedAssetId: assetId,
      });
    }
  };

  handleManualPlay = event => {
    event.stopPropagation();

    if (!this.player) {
      this.setState({ shouldShowPlayButton: false });
      return;
    }

    this.setState({
      playState: PLAY_STATE.Play,
      shouldShowPlayButton: false,
    });
    const playPromise = this.player.play?.();
    playPromise?.catch?.(error => {
      this.setState({
        shouldShowPlayButton: true,
        playState: PLAY_STATE.Pause,
      });
      withScope(scope => {
        scope.setFingerprint(['post-video-player', error.name]);
        scope.setExtra('action', 'manual play button clicked');
        scope.setExtra('playState', this.state.playState);
        captureException(error);
      });
    });
  };

  updateTimeDisplay = throttle(() => {
    if (this.player) this.setState({ currentTime: this.player?.currentTime() });
  }, 200); // TODO: remote config

  handleTimeUpdate = () => {
    this.updateTimeDisplay();
  };

  handleEnded = () => {
    const duration = this.player?.duration?.() || this.state.duration;
    this.setState({
      currentTime: Number.isFinite(duration)
        ? duration
        : this.state.currentTime,
      playState: PLAY_STATE.Ended,
    });
  };

  handlePause = () => {
    const { isVideo, isActive, timestampOffsetMilliseconds } = this.props;
    if (isVideo && isActive) {
      this.props.trackPlayerEvent({
        type: EventTypes.PLAYER_STOPPED,
        timestamp: Date.now() + timestampOffsetMilliseconds,
      });
      this.handleEngagementData({ isPlaying: false });
    }
    if (this.state.playState === PLAY_STATE.Ended) return;
    this.setState({ playState: PLAY_STATE.Pause });
  };

  handlePlaying = () => {
    const {
      onMediaPlay,
      isActive,
      shouldAutoplayPost,
      timestampOffsetMilliseconds,
    } = this.props;
    // Not only videos, images should start recording from here.
    // Otherwise, it would miss tracking if the first post is an image.
    if (isActive && shouldAutoplayPost) {
      this.props.trackPlayerEvent({
        type: EventTypes.PLAYER_STARTED,
        timestamp: Date.now() + timestampOffsetMilliseconds,
      });
      this.handleEngagementData({ isPlaying: true, isPlayerVisible: true });
    }
    onMediaPlay();
    this.setState({
      playState: PLAY_STATE.Playing,
      shouldShowPlayButton: false,
      hasMediaPlayed: true,
    });
  };

  handleWaiting = () => this.setState({ playState: PLAY_STATE.Waiting });

  handleOnClick = e => {
    const { isVideo, canViewMedia } = this.props;
    if (!isVideo && canViewMedia) {
      this.handleImageOnClick(e);
    }
  };

  attachListeners() {
    this.player.on('loadedmetadata', this.handleLoadedMetadata);
    this.player.on('timeupdate', this.handleTimeUpdate);
    this.player.on('ended', this.handleEnded);
    this.player.on('pause', this.handlePause);
    this.player.on('playing', this.handlePlaying);
    this.player.on('waiting', this.handleWaiting);
  }

  unattachListeners() {
    this.player.off('loadedmetadata', this.handleLoadedMetadata);
    this.player.off('timeupdate', this.handleTimeUpdate);
    this.player.off('ended', this.handleEnded);
    this.player.off('pause', this.handlePause);
    this.player.off('playing', this.handlePlaying);
    this.player.off('waiting', this.handleWaiting);
  }

  resetPlayerAndPosterStatus = () => {
    if (this.player) {
      // (2024.06)
      // Just like Shorts, the Following has multiple player wrappers,
      // which means that it will release and acquire player instance multiple times.
      // The old implementation of Shorts and Following depends on `componentWillUnmount` in withPlayerTrackingEvent,
      // but it won't work since we've adopted singleton mode.
      // Specifically, at that moment, the player instance is not necessarily held by non-active player wrappers.
      // That's why we have to handle this here.
      const { trackPlayerEvent, timestampOffsetMilliseconds } = this.props;
      trackPlayerEvent({
        type: EventTypes.PLAYER_STOPPED,
        timestamp: Date.now() + timestampOffsetMilliseconds,
      });
      trackPlayerEvent({
        type: EventTypes.PLAYER_HIDDEN,
        timestamp: Date.now() + timestampOffsetMilliseconds,
      });
      this.handleEngagementData({ isPlaying: false, isPlayerVisible: false });

      this.unattachListeners();
      this.player = null;
    }
    clearTimeout(this.timeout);
    this.setState({
      // With the singleton implementation,
      // the video would be released instead of paused when it shouldn't play.
      // That's why we reset the `currentTime` here.
      currentTime: 0,
      hasMediaPlayed: false,
      shouldHidePoster: false,
      shouldShowPlayButton: false,
      playState: PLAY_STATE.Pause,
    });
  };

  componentDidUpdate(prevProps, prevState) {
    const {
      shouldAutoplayPost,
      isActive,
      isVideo,
      resizePostMediaHeight,
      canViewMedia,
      trackPlayerEvent,
      timestampOffsetMilliseconds,
    } = this.props;
    const { hasMediaPlayed } = this.state;
    if (isActive !== prevProps.isActive) {
      if (isActive) {
        resizePostMediaHeight({ target: this.posterTarget });
      }
      if (!isActive) {
        this.resetPlayerAndPosterStatus();
      }
    }

    if (shouldAutoplayPost !== prevProps.shouldAutoplayPost) {
      if (!shouldAutoplayPost) {
        this.resetPlayerAndPosterStatus();
      }

      this.handleCanPlay();
    }
    if (canViewMedia && canViewMedia !== prevProps.canViewMedia) {
      this.setState({
        hasMediaPlayed: false,
        shouldHidePoster: false,
      });
      this.handleCanPlay();

      if (this.player) {
        trackPlayerEvent({
          type: EventTypes.PLAYER_STOPPED,
          timestamp: Date.now() + timestampOffsetMilliseconds,
        });
        this.handleEngagementData({ isPlaying: false });
      }
    }

    if (hasMediaPlayed && hasMediaPlayed !== prevState.hasMediaPlayed) {
      this.timeout = setTimeout(() => {
        // Delay to hide poster placeholder to avoid the flash by video css turn to display
        this.setState({
          shouldHidePoster: true,
        });
      }, 500); // delay + opacity transition time // TODO: remote config
    }

    if (
      !isVideo &&
      isActive &&
      this.player &&
      !shouldAutoplayPost &&
      prevProps.shouldAutoplayPost
    ) {
      trackPlayerEvent({
        type: EventTypes.PLAYER_STOPPED,
        timestamp: Date.now() + timestampOffsetMilliseconds,
      });
      this.handleEngagementData({ isPlaying: false });
    }
  }

  componentWillUnmount() {
    if (this.player) {
      this.unattachListeners();
      this.player = null;
    }
    clearTimeout(this.timeout);
  }

  renderPlayer = () => {
    const {
      postId,
      assetId,
      // thumbnail,
      canViewMedia,
      captionText,
      isVideo,
      isActive,
      shouldAutoplayPost,
      isIntersected,
      isFullscreenModalOpened,
      loadMode,
      enabledImageFullscreenModal,
    } = this.props;
    const { hasMediaPlayed } = this.state;

    // wait for this PR to enable webp/avif srcset
    // https://github.com/videojs/video.js/pull/8052
    // const srcSet = getPublicSrcSet({
    //   href: thumbnail,
    //   size: 256,
    //   format: this.posterFormat,
    // });

    const nextVideoOption = { autoplay: false };
    if (
      Object.keys(nextVideoOption).length ===
      Object.keys(this.videoOption).length
    ) {
      const isOptionChanged = Object.entries(nextVideoOption).some(
        ([key, value]) => value !== this.videoOption[key]
      );
      if (isOptionChanged) this.videoOption = nextVideoOption;
    } else {
      this.videoOption = nextVideoOption;
    }

    const nextImageOption = {
      loop: false,
      // 2023.02.23
      // Get rid of the poster to avoid the image out of shape on MAC's Safari
      // poster: srcSet.src,
    };
    if (
      Object.keys(nextImageOption).length ===
      Object.keys(this.imageOption).length
    ) {
      const isOptionChanged = Object.entries(nextImageOption).some(
        ([key, value]) => value !== this.imageOption[key]
      );
      if (isOptionChanged) this.imageOption = nextImageOption;
    } else {
      this.imageOption = nextImageOption;
    }

    const shouldMountPlayer =
      isActive && (isIntersected || (loadMode && loadMode !== LoadMode.NONE));

    return (
      shouldMountPlayer && (
        <PlayerWrapper hasMediaPlayed={hasMediaPlayed}>
          {isVideo ? (
            <FlixPlayer
              type="post"
              messageId={postId}
              assetId={assetId}
              // Hide flix player unlock button since post already have unlock button.
              shouldShowUnlock={false}
              shouldAutoplayPost={shouldAutoplayPost}
              onSetup={this.handlePlayerSetup}
              shouldShowControls={canViewMedia} // isMoving detection and disappear time controled by it self
              shouldUseSplash={false}
              disableTrackPlayingTime={true}
              option={this.videoOption}
              loadMode={shouldAutoplayPost ? LoadMode.ACTIVE : loadMode}
            />
          ) : (
            <ImagePlayer>
              <MessageVideoPlayer
                messageId={postId}
                assetId={assetId}
                shouldAutoplayPost={shouldAutoplayPost}
                isMuted={true}
                onSetup={this.handlePlayerSetup}
                alt={captionText}
                option={this.imageOption}
                shouldUseTrailer={false}
                shouldUseSplash={false}
                drmFeatureName={DrmFeatureName.POST}
                isFullscreenModalOpened={isFullscreenModalOpened}
                enabledImageFullscreenModal={enabledImageFullscreenModal}
                loadMode={LoadMode.ACTIVE}
              />
              <ImageGestureDetector onClick={this.handleOnClick} />
            </ImagePlayer>
          )}
        </PlayerWrapper>
      )
    );
  };

  renderPoster = () => {
    const { objectFit, thumbnail, posterSize } = this.props;
    const { hasMediaPlayed, shouldHidePoster } = this.state;

    /*
    const [avifSrcSet, webpSrcSet, jpgSrcSet] = [
      MediaAssetFormat.AVIF,
      MediaAssetFormat.WEBP,
      MediaAssetFormat.JPG,
    ].map(format => getPublicSrcSet({ href: thumbnail, size: 256, format }));
    */
    // For using same asset with video player poster,
    // wait for this PR to enable webp/avif srcset
    // https://github.com/videojs/video.js/pull/8052
    const srcSet = getPublicSrcSet({
      href: thumbnail,
      size: posterSize,
      format: this.posterFormat,
    });

    return (
      <PlayerPoster
        shouldHidePoster={shouldHidePoster}
        hasMediaPlayed={hasMediaPlayed}
        objectFit={objectFit}
      >
        <StatefulImage>
          <picture data-key="target">
            <DecryptionWrapper resourceUrl={srcSet.src}>
              {/*
              <source srcSet={avifSrcSet} type="image/avif" />
              <source srcSet={webpSrcSet} type="image/webp" />
              */}
              <img
                src={srcSet.src}
                alt="poster"
                onLoad={e => {
                  this.posterTarget = e.target;
                  // Backfill missing resize observed for AES image posts.
                  if (this.props.isActive) {
                    this.props.resizePostMediaHeight({ target: e.target });
                  }
                }}
              />
            </DecryptionWrapper>
          </picture>
        </StatefulImage>
      </PlayerPoster>
    );
  };

  renderRemainingTime = () => {
    const { duration: durationFromState, currentTime } = this.state;
    const { duration: durationFromProps } = this.props;

    let duration = durationFromState;
    if (duration === 0 && durationFromProps) duration = durationFromProps;

    let remainingTimeString = '--:--';
    if (Number.isFinite(duration)) {
      remainingTimeString = format(
        Math.max((duration - currentTime) * 1000, 0),
        getTimeFormat(duration)
      );
    }
    return (
      <RemainingTime>
        <PlayButtonImage src={GridVideoSource} />
        {remainingTimeString}
      </RemainingTime>
    );
  };

  renderManualPlayButton = () => {
    const { canViewMedia, isVideo } = this.props;
    const { shouldShowPlayButton } = this.state;

    if (isVideo && canViewMedia && shouldShowPlayButton) {
      return (
        <ManualPlayWrapper onClick={this.handleManualPlay}>
          <img src={PlayIcon} alt="" />
        </ManualPlayWrapper>
      );
    }

    return null;
  };

  render() {
    const {
      assetId,
      mediaType,
      canViewMedia,
      isVideo,
      shouldShowRemainingTime,
    } = this.props;

    return (
      <StyledPostVideoPlayer
        data-asset_id={assetId}
        data-media_type={mediaType}
        data-can_view_media={canViewMedia}
      >
        {this.renderPlayer()}
        {this.renderPoster()}
        {!canViewMedia && <Overlay />}
        {isVideo && shouldShowRemainingTime && this.renderRemainingTime()}
        {this.renderManualPlayButton()}
      </StyledPostVideoPlayer>
    );
  }
}

PostVideoPlayer.propTypes = {
  postId: PropTypes.string,
  assetId: PropTypes.string,
  canViewMedia: PropTypes.bool,
  mediaType: PropTypes.string,
  isVideo: PropTypes.bool,
  captionText: PropTypes.string,
  togglePostAssetFullscreenModal: PropTypes.func,
  setCurrentAutoplayPostId: PropTypes.func,
  isFullscreenModalOpened: PropTypes.bool,
  isActive: PropTypes.bool,
  enabledImageFullscreenModal: PropTypes.bool,
  shouldAutoplayPost: PropTypes.bool,
  shouldShowRemainingTime: PropTypes.bool,
  resizePostMediaHeight: PropTypes.func,
  onMediaPlay: PropTypes.func,
  objectFit: PropTypes.string,
  thumbnail: PropTypes.string,
  isIntersected: PropTypes.bool,
  loadMode: PropTypes.string,
  duration: PropTypes.number,
  posterSize: PropTypes.number,
  timestampOffsetMilliseconds: PropTypes.number,
  trackPlayerEvent: PropTypes.func,
  updateEngagementData: PropTypes.func,
  isFree: PropTypes.bool,
  isNewlyUnlocked: PropTypes.bool,
  viewScope: PropTypes.string,
  senderId: PropTypes.string,
  senderName: PropTypes.string,
};

PostVideoPlayer.defaultProps = {
  postId: '',
  assetId: '',
  canViewMedia: false,
  mediaType: null,
  isVideo: false,
  isMuted: true,
  captionText: null,
  togglePostAssetFullscreenModal: () => null,
  setCurrentAutoplayPostId: () => null,
  isFullscreenModalOpened: false,
  isActive: false,
  enabledImageFullscreenModal: false,
  shouldAutoplayPost: false,
  shouldShowRemainingTime: true,
  resizePostMediaHeight: () => null,
  onMediaPlay: () => null,
  objectFit: '',
  thumbnail: null,
  isIntersected: false,
  loadMode: LoadMode.NONE,
  duration: 0,
  timestampOffsetMilliseconds: 0,
  posterSize: null,
  trackPlayerEvent: () => null,
  updateEngagementData: () => null,
  isFree: null,
  isNewlyUnlocked: null,
  viewScope: '',
  senderId: '',
  senderName: '',
};

const StyledPostVideoPlayer = styled.div`
  position: relative;
  width: inherit;
  height: inherit;
  background-color: #191919;

  div[class='vjs-control-bar'] {
    display: none;
  }
`;

const RemainingTime = styled.span`
  position: absolute;
  top: 12px;
  right: 18px;
  font-size: 16px;
  line-height: 16px;
  text-shadow: 0 0 4px rgba(0, 0, 0, 0.5);

  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;

  ${media.mobile`
      top: 13px;
      right: 16px;
      font-size: 12px;
      line-height: 12px;
  `};
`;

const PlayButtonImage = styled.img`
  width: 14px;
  height: 14px;
  margin-right: 4px;
`;

const PlayerWrapper = styled.div`
  visibility: ${({ hasMediaPlayed }) =>
    hasMediaPlayed ? 'visible' : 'hidden'};

  position: relative;
  width: 100%;
  height: 100%;
`;

const ImagePlayer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  cursor: pointer;
`;

const Overlay = styled.div`
  pointer-events: none;
  position: absolute;
  top: 0px;
  bottom: 0px;
  left: 0px;
  right: 0px;
  z-index: 1;
  animation: 0.6s forwards ${FadeIn};
  background-image: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.5),
    rgba(0, 0, 0, 0.1) 20%,
    rgba(0, 0, 0, 0.04) 25%,
    rgba(0, 0, 0, 0.04) 33%,
    rgba(0, 0, 0, 0.5)
  );
`;

const PlayerPoster = styled.div.attrs(
  ({ shouldHidePoster, hasMediaPlayed }) => ({
    style: {
      display: shouldHidePoster ? 'none' : 'block',
      opacity: hasMediaPlayed ? 0 : 1,
    },
  })
)`
  position: absolute;
  z-index: 1;
  left: 0px;
  top: 0px;
  bottom: 0px;
  right: 0px;
  transition: linear 0.4s opacity;
  img {
    width: 100%;
    height: 100%;

    ${({ objectFit }) => {
      if (objectFit) {
        return css`
          object-fit: ${objectFit};
        `;
      }

      return css`
        object-fit: contain;

        ${media.tablet`
          object-fit: cover;
        `}
      `;
    }}
  }
`;

const ImageGestureDetector = styled.div`
  opacity: 0;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;

const ManualPlayWrapper = styled.button`
  ${ResetButtonStyle}
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 5;
  & img {
    width: 100%;
    height: 100%;
  }
`;

export default withPlayerTrackingEvent(PostVideoPlayer, VIDEO_CATEGORY.POST);
